import {
  PAGE_USAGE_MAX_TRACKED,
  PageUsagePreferencesSchema,
  type PageUsagePreferences,
} from '@erp/rpc';

export type PageUsage = PageUsagePreferences['pages'];

/** Usage older than this counts half as much as usage today. */
const HALF_LIFE_DAYS = 14;
const MS_PER_DAY = 86_400_000;

/** Routes that are entry points rather than destinations the user chose. */
const UNTRACKED_PATHS = new Set(['/', '/home', '/welcome']);

export function isTrackedPath(path: string | undefined): path is string {
  return !!path && path.startsWith('/') && !UNTRACKED_PATHS.has(path);
}

/** Reads the stored preference, treating anything malformed as "no history". */
export function parsePageUsage(value: unknown): PageUsage {
  const parsed = PageUsagePreferencesSchema.safeParse(value);
  return parsed.success ? parsed.data.pages : {};
}

/** Visit count decayed by age, so recent habits outrank old ones. */
export function frecency(
  entry: { count: number; lastVisitedAt: string },
  now: Date,
): number {
  const ageDays = Math.max(
    0,
    (now.getTime() - new Date(entry.lastVisitedAt).getTime()) / MS_PER_DAY,
  );
  return entry.count * 0.5 ** (ageDays / HALF_LIFE_DAYS);
}

/** Records one visit, dropping the least relevant pages beyond the cap. */
export function recordVisit(
  usage: PageUsage,
  path: string,
  now: Date,
): PageUsage {
  const previous = usage[path];
  const next: PageUsage = {
    ...usage,
    [path]: {
      count: (previous?.count ?? 0) + 1,
      lastVisitedAt: now.toISOString(),
    },
  };
  const paths = Object.keys(next);
  if (paths.length <= PAGE_USAGE_MAX_TRACKED) return next;

  const keep = paths
    .filter((candidate) => candidate !== path)
    .sort(
      (a, b) =>
        frecency(next[b] ?? { count: 0, lastVisitedAt: '' }, now) -
        frecency(next[a] ?? { count: 0, lastVisitedAt: '' }, now),
    )
    .slice(0, PAGE_USAGE_MAX_TRACKED - 1);
  const pruned: PageUsage = {};
  for (const key of [path, ...keep]) {
    const entry = next[key];
    if (entry) pruned[key] = entry;
  }
  return pruned;
}

export interface PageActivity {
  score: number;
  lastVisitedAt: string;
}

/**
 * Activity for a destination page. Visits to its detail routes
 * (`/orders/$orderId` under `/orders`) count toward it.
 */
export function activityFor(
  usage: PageUsage,
  path: string,
  now: Date,
): PageActivity | undefined {
  let score = 0;
  let lastVisitedAt: string | undefined;
  for (const [key, entry] of Object.entries(usage)) {
    if (key !== path && !key.startsWith(`${path}/`)) continue;
    score += frecency(entry, now);
    if (!lastVisitedAt || entry.lastVisitedAt > lastVisitedAt) {
      lastVisitedAt = entry.lastVisitedAt;
    }
  }
  return lastVisitedAt ? { score, lastVisitedAt } : undefined;
}
